import { applySnapshot, types } from 'mobx-state-tree'
import { flow } from "modules/common/models/flow"

import { OutsourcerDictionary, initialState as outsourcerState } from 'modules/agents/outsourcers/models/outsourcer-dictionary'
import { Notificator } from 'modules/common/models/notificator'
import { Queryable } from 'modules/common/models/queryable'
import { Transport } from 'modules/common/models/transport'
import { apiUrls } from 'modules/common/services/communication/urls'
import { ProjectPortfolioDictionary, initialState as portfolioState } from 'modules/dictionaries/project-portfolios/models/project-portfolio-dictionary'
import { EmployerDictionary, initialState as employerState } from 'modules/spending/employee/models/employee-dictionary'
import { texts } from 'modules/common/texts'
import { base64ToBlob } from 'modules/common/services/files'
import saveAs from 'file-saver'
import { PendingSignatureItemType } from 'modules/main/models/order-content-task-signatures'

const SignatureInfo = types
  .model({
    employerId: types.string,
    employerName: types.string,
    signerType: types.string,
    signed: types.boolean,
  })
  .named("SignatureInfo")

const NoRoleInfo = types
  .model({
    employerId: types.string,
    employerName: types.string,
    signed: types.boolean,
  })
  .named("NoRoleInfo")

export const EmployeeSignatureFile = types
  .model({
    id: types.string,
    fileId: types.string,
    fileName: types.string,
    version: types.number,
    date: types.maybe(types.string), // Сделали опциональным
    time: types.maybe(types.string), // Сделали опциональным
    createdAt: types.maybe(types.string), // Сделали опциональным
    rowVersion: types.number,
    employers: types.array(SignatureInfo),
    noRoles: types.array(NoRoleInfo)
  })
  .named("EmployeeSignatureFile")

export type EmployeeSignatureFileType = typeof EmployeeSignatureFile.Type

export const EmployeeSignaturesList = types
  .compose(
    Transport,
    Notificator,
    Queryable,
    types.model({
      files: types.array(EmployeeSignatureFile),
      employee: EmployerDictionary,
      outsourcers: OutsourcerDictionary,
      portfolios: ProjectPortfolioDictionary
    })
  ).actions((self) => ({
    init: flow(function* () {
      try {
        yield self.employee.load()
        yield self.outsourcers.load()
        yield self.portfolios.load()
      }
      catch (er) { }
    }),
    load: flow(function* () {
      try {
        const url = apiUrls.employee.signaturesFiles()
        const newData: EmployeeSignatureFileType[] = yield self.transport.get<any>(url)
        console.info(`load ${newData}`)
        applySnapshot(self.files, newData)
      } catch (er) {
        self.notify.error(er)
        return null
      } finally {
      }
    }),
    submitSignature: flow(function* (
      signature: PendingSignatureItemType,
      signatureValue: string,
      rowVersion: number,
      userId?: string | null
    ) {
      try {
        const payload = {
          signatureId: signature.signatureId,
          signerType: signature.signerType,
          fileId: signature.fileId,
          signature: signatureValue,
          signatureType: 'detached',
          orderId: signature.orderId,
          rowVersion: rowVersion,
          userId: userId || undefined, // за кого подписываем (владелец файла — за назначенного)
        }

        // Бэк возвращает обновленный EmployeeSignatureFile
        const updatedFile: EmployeeSignatureFileType = yield self.transport.post(
          apiUrls.employee.submitSignature,
          payload
        )

        if (updatedFile) {
          // Находим индекс файла в списке по fileId и version
          const index = self.files.findIndex(
            (f) => f.fileId === updatedFile.fileId && f.version === updatedFile.version
          )

          if (index !== -1) {
            // Заменяем старый файл на обновленный
            applySnapshot(self.files[index], updatedFile)
          } else {
            // Если файл не найден - добавляем новый
            self.files.push(updatedFile)
          }

          self.notify.success("Документ успешно подписан")
        }

        return updatedFile
      } catch (er) {
        self.notify.error(`Ошибка отправки подписи: ${er}`)
        throw er
      } finally {
        // self.signingInProgress = false
      }
    }),
    /**
     * Прикрепление внешнего файла подписи (.sig): вливается в общий контейнер
     * документа за указанного подписанта или без роли (тогда имя берётся из сертификата)
     */
    attachSignature: flow(function* (
      signature: PendingSignatureItemType,
      signatureValue: string,
      userId?: string | null,
      signerNames?: string[]
    ) {
      try {
        const payload = {
          signatureId: signature.signatureId,
          signerType: signature.signerType,
          fileId: signature.fileId,
          signature: signatureValue,
          signatureType: 'detached',
          rowVersion: 0,
          external: true,
          userId: userId || undefined,
          signerName: signerNames && signerNames.length > 0 ? signerNames.join(', ') : undefined,
        }

        const updatedFile: EmployeeSignatureFileType = yield self.transport.post(
          apiUrls.employee.submitSignature,
          payload
        )

        if (updatedFile) {
          const index = self.files.findIndex(
            (f) => f.fileId === updatedFile.fileId && f.version === updatedFile.version
          )

          if (index !== -1) {
            applySnapshot(self.files[index], updatedFile)
          } else {
            self.files.push(updatedFile)
          }

          self.notify.success("Подпись успешно прикреплена")
        }

        return updatedFile
      } catch (er) {
        self.notify.error(`Ошибка прикрепления подписи: ${er}`)
        throw er
      }
    }),
    uploadFile: flow(function* (file: File) {
      try {
        const model = new FormData()

        model.append("file", file)
        model.append("accept", "*")

        const result: any = yield self.transport.post<any>(apiUrls.application.files.upload, model)
        const { id, previewMimeType, mimeType } = result
        const fileBase: FileBase = { fileId: id, fileName: file.name, previewMimeType, mimeType }
        return fileBase
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),
    saveFiles: flow(function* (files: any[]) {
      try {
        const result = yield self.transport.post<any>(
          apiUrls.employee.saveFiles(),
          {
            files,
          }
        )
        if (result) {
          self.notify.success(texts.messages.saved)
          applySnapshot(self.files, result)
        }

        return true

      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
    downloadVersion: flow(function* (fileId: string, version: number) {
      try {
        const result = yield self.transport.get<any>(
          apiUrls.employee.downloadSignatureVersion(fileId, version)
        )
        if (result) {
          const blob: any = yield base64ToBlob(result.content || "", result.mimeType)
          saveAs(blob, result.name)
        }
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
    removeVersion: flow(function* (fileId: string, version: number) {
      try {
        const result = yield self.transport.delete<any>(apiUrls.employee.removeFileVersion(), {
          data: {
            fileId,
            version,
          },
        })
        if (result) {
          applySnapshot(self.files, result)
        }
        self.notify.success(texts.messages.saved)
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
    saveDevelopers: flow(function* (
      fileId: string,
      fileName: string,
      version: number,
      worked: any[],
      legal: any[],
      hasLegal: boolean,
    ) {
      try {
        if (!hasLegal) {
          legal = []
        }

        if (worked.length === 0 && (!hasLegal || legal.length === 0)) {
          self.notify.error("Все поля должны быть заполнены!")
          return false
        }
        const normalizedLegal = legal.map((item) => ({
          ...item,
          type: "legal" // Принудительно устанавливаем тип "legal"
        }))
        // Сохраняем подписантов для конкретного файла
        const payload = {
          version,
          fileId,
          fileName,
          worked,
          legal: normalizedLegal
        }

        try {
          const result = yield self.transport.post<any>(
            apiUrls.employee.saveSigners(),
            payload
          )

          if (result) {
            const existingIndex = self.files.findIndex(
              (file) => file.fileId === result.fileId && file.version === result.version
            )
            if (existingIndex !== -1) {
              // Применяем снапшот к существующему файлу
              applySnapshot(self.files[existingIndex], result)
            }
            self.notify.success(texts.messages.saved)
            return true
          } else {
            self.notify.error("Не удалось сохранить подписантов")
            return false
          }
        } catch (error) {
          self.notify.error(`${(error as any).message}`)
          return false
        }

      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
  })).named("EmployeeSignaturesList")

export type EmployeeSignaturesListType = typeof EmployeeSignaturesList.Type
export type EmployeeSignaturesListSnapshotType = typeof EmployeeSignaturesList.SnapshotType

export type EmployeeSignatureInfoType = typeof SignatureInfo.Type
export type EmployeeSignatureInfoTypeSnapshotType = typeof SignatureInfo.SnapshotType

export const initialState = (): EmployeeSignaturesListSnapshotType => ({
  files: [],
  employee: employerState(),
  outsourcers: outsourcerState(),
  portfolios: portfolioState(),
  pureQuery: "",
  query: "",
})